/*
 * This file is part of glowingblue/orell-rezensionen.
 *
 * Copyright (c) 2023 Glowing Blue AG.
 * Authors: Rafael Horvat.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import app from 'flarum/forum/app';
import { components, utils } from '@glowingblue-orell-thalia-common';
import type { HomeDiscussionsAttrs } from '@glowingblue-orell-thalia-common/components/HomeDiscussions';

const { HomeDiscussions } = components;
const { childIndex } = utils.vdom;
import type { VdomParent } from '@glowingblue-orell-thalia-common/utils/vdom';
import ReviewItem from './ReviewItem';
import CreateReviewPrimaryButton from './CreateReviewPrimaryButton';
import ReviewsCoverflow from './ReviewsCoverflow';
import SubtreeRetainer from 'flarum/common/utils/SubtreeRetainer';
import type Mithril from 'mithril';
import type Discussion from 'flarum/common/models/Discussion';

export default class HomeReviews extends HomeDiscussions {
	subtree!: SubtreeRetainer;

	oninit(vnode: Mithril.Vnode<HomeDiscussionsAttrs, this>) {
		super.oninit(vnode);

		this.subtree = new SubtreeRetainer(
			() => this.discussions.hasItems(),
			() => this.discussions.isInitialLoading()
		);
	}

	onbeforeupdate(vnode: Mithril.VnodeDOM<HomeDiscussionsAttrs, this>) {
		super.onbeforeupdate(vnode);

		return this.subtree.needsRebuild();
	}

	view(vnode?: Mithril.Vnode<HomeDiscussionsAttrs, this>) {
		if (this.discussions.hasItems() || this.discussions.isInitialLoading()) {
			// `HomeDiscussions` types `view()` without parameters, though Mithril passes the vnode.
			let _vnode = (super.view as (v?: Mithril.Vnode<HomeDiscussionsAttrs, this>) => JSX.Element)(vnode);
			let listIndex = childIndex(_vnode as VdomParent, 'HomeDiscussions-discussions');

			if (listIndex) {
				(_vnode.children as Mithril.Children[])[listIndex as number] = (
					<ReviewsCoverflow discussions={this.discussions.getPages().flatMap((page) => page.items)} />
				);

				return _vnode;
			} else {
				return _vnode;
			}
		} else {
			return (super.view as (v?: Mithril.Vnode<HomeDiscussionsAttrs, this>) => JSX.Element)(vnode);
		}
	}

	get titleText() {
		return app.translator.trans('glowingblue-orell-rezensionen.forum.home.reviews.title');
	}

	get emptyText() {
		return app.translator.trans('glowingblue-orell-rezensionen.forum.review_list.empty_text');
	}

	get button() {
		return <CreateReviewPrimaryButton />;
	}

	listItem(discussion: Discussion) {
		return (
			<li key={discussion.id()} data-id={discussion.id()}>
				<ReviewItem discussion={discussion} />
			</li>
		);
	}

	skeletonItem() {
		return <div className="DiscussionListItem ReviewItem HomeReviews-skeleton"></div>;
	}

	get limit() {
		return 10;
	}

	get allLinkText() {
		return app.translator.trans('glowingblue-orell-rezensionen.forum.home.reviews.all_link');
	}

	get allLinkHref() {
		return app.route('reviews');
	}
}
